import { FormikErrors } from 'formik';
import { KeyedMutator } from 'swr';

import { IOption } from '@shared/interfaces';
import { ILeadData } from '@modules/ContactManager/components/Header/interfaces';
import { Address2, IDeal, IDealSummary } from '../Deals/interfaces';
import { NContact } from '../Contact/validation';

export enum VALUATION_TYPES {
  Kbb = 'Kbb',
  Nada = 'Nada',
  Msrp = 'Msrp',
  Bumper = 'Bumper',
}

export enum VALUATION_TEXTS {
  Kbb = 'Kbb',
  Nada = 'JD Power',
  Msrp = 'Msrp',
  Bumper = 'Bumper',
}

export const VALUATION_TYPE_OPTIONS: IOption[] = [
  {
    value: VALUATION_TYPES.Kbb,
    label: VALUATION_TYPES.Kbb,
  },
  {
    value: VALUATION_TYPES.Nada,
    label: VALUATION_TEXTS.Nada,
  },
  {
    value: VALUATION_TYPES.Msrp,
    label: VALUATION_TYPES.Msrp,
  },
  {
    value: VALUATION_TYPES.Bumper,
    label: VALUATION_TYPES.Bumper,
  },
];

export enum PRODUCT_TYPES {
  Gap = 'Gap',
  Vsc = 'Vsc',
}

export interface IAssetOption {
  id: string;
  display: string;
}

export interface ILienHolder {
  id: string;
  name: string;
  description: string;
  numericId: number;
  showBanner: boolean;
  address: NContact.Address2;
  acumaticaVendorId: string;
  phoneNumber: string;
  acceleratedTitlePartnerCode: string | undefined;
  acceleratedTitlePartnerName: string | undefined;
  title?: string;
}

export interface IAssetRegisterForm {
  value: IAsset;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  setFieldValue: (field: string, value: any, shouldValidate?: boolean) => Promise<void | FormikErrors<any>>;
  validateField: (field: string) => void;
  deal: IDeal;
}

export interface IAsset {
  id: string;
  assetType: string;
  vin: string;
  year: number | null;
  make: string;
  model: string;
  kbbTrim: string;
  nadaTrim: string;
  bodyStyle: string;
  mileage?: number | null;
  assetCondition: string;
  color: string;
  assetUsage: string;
  msrp: number;
  isBrandedTitle?: boolean;
  assetLienHolder?: IAssetLienHolder;
  assetRegistration?: IAssetRegistration;
  assetBrand?: IAssetBrand;
  assetInsurance?: IAssetInsurance;
  assetValuations?: IAssetValuation[];
  vscRefund?: number;
  gapRefund?: number;
  contactId: string;
  coContactId?:string;
  isAssetOwner?: string | null;
  isPlateToVinSuccess: boolean;
  isVinLookupSuccess: boolean;
  assetValidations: IAssetValidations;
  vehicleFeature: IVehicleFeature;
}

export interface IFormAsset extends IAsset {
  kbb?: IAssetValuation | null;
  nada?: IAssetValuation | null;
  bumper?: IAssetValuation | null;
  bookOutDate?: string | null;
}

export interface ILoan {
  loanAmount: number | string | null;
  term: number | string | null;
  rate: number | string | null;
  monthlyPayment: number | string | null;
  perDiem: number | string | null;
  nextPaymentDate: string | null;
}

export interface IAddress {
  streetLine1: string;
  streetLine2: string;
  city: string;
  state: string | null;
  zip: string;
  type: string;
  county: string;
}

export interface ICurrentProduct {
  refundAmount: number;
  productType: string;
}

export interface IAssetLienHolder {
  name: string;
  lienHolderId: string | null;
  remainingLoanAmount: number;
  remainingTerm: number;
  goodDate: string | null;
  loan: ILoan;
  address: IAddress;
  currentProducts: ICurrentProduct[];
  accountNumber: string;
  phoneNumber: string;
  isFinalPayoffQuote: boolean;
  isPayoffSubmitted: boolean;
  isPayoffCancelled: boolean;
  isAcceleratedTitleSupported: boolean | null;
  acceleratedTitleTitleIsReleasable: boolean | null;
  acceleratedTitlePartnerCode?: string | null;
  acceleratedTitleTransactionCharges?: number | null;
  acceleratedTitleTaxFee?: number | null;
  acceleratedTitleTitleFormat?: string | null;
  acceleratedTitleTitleID?: string | null;
  acceleratedTitleTitleState?: string | null;
}

export interface IAssetRegistration {
  primaryOwner: string;
  secondaryOwner: string;
  number: string;
  state: string;
  expirationDate: string | null;
  registeredOwners: number;
  titleNumber: string;
  registrationNumber: string;
  isPrimaryChange: boolean;
  plateNumber?: string;
  plateState?: string;
  titleState?: string;
}

export interface IAssetBrand {
  BrandPresent: string;
  BrandReason: string;
  BrandPresentIndicator: boolean;
  BrandedTitle: boolean;
  branderState: string[];
  brandedStateList: string[];
  brandName: string;
  brandDescription: string;
}

export interface IAssetInsurance {
  company: string;
  phoneNumber: string;
  policyNumber: string;
  effectiveDate: string | null;
  expirationDate: string | null;
}

export interface IAssetValuation {
  type: string;
  baseRetailAmount?: number | string | null;
  baseTradeInAmount?: number | string | null;
  baseWholesaleAmount?: number | string | null;
  baseFinanceExpressAmount?: number | string | null;
  bookOut?: IBookOut;
  high?: number | string | null;
  avg?: number | string | null;
  low?: number | string | null;
}

export interface  IAssetValidations {
  isLiftKit: string | null;
  isSixInchLiftKit: string | null;
  isHEV: string | null;
  doesOversizeUndersizeTire: string | null;
}

export interface IBookOut {
  createdDate: string;
}

export interface IVehicleFeaturesModalProps {
  handleClose: () => void;
  valuationType: string | null;
  pickedDealId?: string;
  zip?: string | null;
  autoSubmitOptionsPopup: boolean;
  handleValuationPull: (isSuccess: boolean) => void;
  vehicleFeatureModalPosition: number;
}

export interface IVehicleFeature {
  retailValue: number;
  tradeInValue: number | null;
  wholesaleValue: number | null;
  financeExpressValue: number | null;
  high: number | null;
  avg: number | null;
  low: number | null;
  vehicleValuation: IVehicleValuation;
  selectedOptions: string[];
  vehicleDetails: IVehicleDetails;
}

export interface IVehicleValuation {
  options: IVehicleValuationOption[];
}

export interface IVehicleValuationOption {
  description: string;
  code: string;
  tradeIn: number | null;
  retailAmount: number | null;
  isIncluded: boolean;
  isSelected: boolean;
  isAdded: boolean;
  accessoryCategory: string;
  isDisabled: boolean;
  id: string;
  wholeSaleAmount: number | null;
  isDefault: boolean;
  isMatchBumper: boolean;
}

export interface INadaFormProps {
  options: IVehicleValuationOption[];
  value: IVehicleFeature;
  handleFeatureSubmit: (value: IVehicleFeature) => void;
}

export interface IKbbFormProps {
  options: IVehicleOption[];
  valuations: IVehicleValuationOption[];
  value: IVehicleFeature;
  handleFeatureSubmit: (value: IVehicleFeature) => void;
}

export interface IVehicleDetails {
  vehicleOptions: IVehicleOption[];
  year: number;
  make: string;
  model: string;
  bodyStyle: string;
  providerVehicleId: number;
  vehicleType: string;
  mileageClass: string;
  baseMsrp: string;
  bodyType: string;
  doors: number;
  trim: string;
  trim2: string;
  driveType: string;
  liters: string;
  engineConfiguration: string;
  cylinders: string;
  inductionType: string;
  transmission: string;
  fuelType: string;
  wheels: string;
  curbWeight: string;
  gvw: string;
  gcw: string;
  ucgSubSegment: string;
  modelNumber: string;
  rollUpVehicleId: string;
  baseCleanTrade: string;
  baseAverageTrade: string;
  baseRoughTrade: string;
  baseCleanRetail: string;
  baseLoan: string;
  averageMileage: string;
  mileageAdjustment: string;
  adjustedCleanTrade: string;
  adjustedAverageTrade: string;
  adjustedrRoughTrade: string;
  adjustedCleanRetail: string;
  adjustedLoan: string;
  maxMileageAdj: string;
  minMileageAdj: string;
  minAdjRetail: string;
  minadjCleanTrade: string;
  minAdjaverageTrade: string;
  minAdjRoughTrade: string;
  minAdjLoan: string;
  minAdjRetailForLoan: string;
  vid: string;
  vehicleClass: string;
  vehicleName: string;
  modelMarketName: string;
  modelYearId: number;
  trimId: number;
  modelPlusTrimName: string;
  bedLength: string;
  genericBodyStyle: string;
  oemBodyStyle: string;
  sortOrder: number;
  isConsumer: boolean;
}

export interface IVehicleOption {
  vehicleOptionId: string;
  vehicleId: number;
  optionType: string;
  optionName: string;
  secondaryName: string;
  detailName: string;
  footer: string;
  categoryName: string;
  categoryGroup: string;
  oemCode: string;
  availability: string;
  sortOrder: number;
  isConsumer: boolean;
  isTypical: boolean;
  isConfigurable: boolean;
  hasRelationships: boolean;
  isVinDecoded: boolean;
  isDisabled: boolean;
  isSelected: boolean;
  isMatchBumper: boolean;
}

export interface IBookResponse {
  bookOutDate: string;
  retailAmount: number;
  tradeInAmount: number;
  wholeSaleAmount: number;
  financeExpressAmount: number;
  content: string;
  filePath: string;
  fileName: string;
}

export interface ITrimsModalProps {
  handleClose: () => void;
  handleSave?: () => void;
  kbbTrims: IVehicleTrim[];
  nadaTrims: IVehicleTrim[];
  kbbFieldName?: string;
  nadaFieldName?: string;
  trimModalPosition: number;
}

export interface IVehicleTrim {
  key: string;
  value: string;
}

export enum ADDFileType {
  Pdf = 'pdf',
  Xml = 'xml',
}

export interface IPartnerResponse {
  partnerCode: string;
  acceleratedTitleSupported: boolean;
}

export interface IPullPayOffQuoteRequest {
  contactId: string;
  assetId: string;
  vin: string;
  accountNo: string;
}

export interface IDealertrackPayoffInfo {
  titleId?: string;
  isATSupported?: boolean;
  atTransactionCharges: number;
  atTaxFee: number;
  atTitleFormat?: string;
  atTitleState?: string;
  atTitleIsReleasable?: boolean;
}

export interface INcicCodeResponse {
  makeCode: string;
  description: string;
}

export interface IOpenLendingApplicationResponse {
  contactId: string;
  dealId: string;
  dealNumericId?: number | null;
  leadId: string;
  coContactId?: string | null;
  userId: string;
  responseHtml: string;
  vin: string;
  loanApplicationResponse: string;
  requestBlobPath: string;
  responseBlobPath: string;
  error: string;
  id: string;
  isDeleted: boolean;
  numericId?: number | null;
}

export interface ICoApplicant extends NContact.CoApplicant {
  isDroppedContact: boolean;
}
export interface AssetsProps {
  assets: IAsset[];
  reloadAssets: KeyedMutator<IAsset[]>;
  validatingAssets: boolean;
  deals?: IDealSummary[];
  coApplicants?: ICoApplicant[];
  onSave: () => void;
  reloadHeaderData: KeyedMutator<ILeadData>;
}

export interface CustomGridItemProps {
  children: React.ReactNode;
}

export interface IPlateToVin {
  dealId: string,
  plateState: string;
  plateNumber: string,
  assetId: string,
  contactId: string;
}

export interface IBumperPlateToVin {
  plateState: string;
  plateNumber: string
}

export interface IPlateToVinResp {
  vin: string;
  make: string;
  model: string;
  year: string;
  isSuccess: boolean;
  message: string;
  plateNumber: string;
  plateState: string;
}

// ------------------------------------------------------------ BumperPlate ------------------------------------------------------------
export interface IBumperPlateResponse {
  bumper_plate: IBumperPlate;
  plate: string;
  state: string;
  is_error: boolean | null;
  error_description: string;
}

export interface IBumperPlate {
  entities: IEntities;
}

export interface IEntities {
  vehicles: IVehicles
}

export interface IMeta {
  confidence: number;
  firstSeenDate: IDate;
  hash: string[];
  isBest: boolean;
  lastSeenDate: string;
  premium : boolean;
  timeSeen: number;
}

export interface IDate {
  full: string;
  parsedDate: IParsedDate;
}

export interface IParsedDate {
  day: number;
  month: number;
  year: number;
}

export interface IVehicles {
  automobiles: IAutomobile[]
}

export interface IAutomobile {
  accidents: IAccident[];
  auctions: IAuction[];
  bodyStyle: string;
  category: string;
  class: string;
  clean: boolean;
  damageHistory: IDamageHistory;
  design: IDesign;
  equipment: IEquipment[];
  estimatedMileage: IEstimatedMileage;
  export: IExport[];
  imaged: IImage[];
  impounds: IImpound[];
  lease: ILease;
  licensePlate: string;
  licensePlateState: string;
  liens: ILien[];
  maintenanceSchedule: IMaintenanceSchedule[];
  make: string;
  meta: IMetaInfo;
  mileage: IMileage;
  model: string;
  nmvtissuccess: boolean;
  options: IOptions[];
  origin : IOrigin;
  ownershipcosts: IOwnershipCosts;
  ownershipHistory: IOwnershipHistory[];
  prices: IPrices;
  recalls: IRecalls[];
  repairCpsts: IRepairCost[];
  safetyrating: ISafetyRating[]
  sales: ISale[];
  salvage: ISalvage[];
  size: string;
  specs: ISpecs;
  theft: ITheft[];
  titlecheck: ITitleCheck[];
  titleregistration: ITitleRegistration[];
  type: string;
  vin: string;
  vinprefix: string;
  warranty: IWarranty[];
  year: number;
}

export interface IWarranty {
  isActive: boolean;
  miles: string;
  months: string;
  type: string
}

export interface ITitleRegistration {
  date: IDate;
  history: IHistory[];
  issuingAuthority: string;
  odometer: number;
  vin: string
}

export interface IHistory {
  date: IDate;
  issuingAuthority: string;
  odometer: number;
  vin: string
}

export interface ITitleCheck {
  code: string;
  date: IDate;
  description: string;
  reportingEntity: IReportingEntity
}

export interface IReportingEntity {
  categoryCode: string;
  categoryText: string;
  contact: IContact;
  id: string;
  name: string;
  source: string;
  website: string
}

export interface IContact {
  addresses: IBumperAddress[];
  emails: IEmail[];
  phones: IPhone[]
}

export interface IPhone {
  carrier: string;
  countryCode: number;
  dialable: boolean;
  extension: string;
  geo: IGeo;
  meta: IMeta;
  number: string;
  type: string
}

export interface IEmail {
  address: string;
  addressMd5: string;
  isDerived: boolean;
  isDisposable: boolean;
  isPublicProvider: boolean;
  meta: IMeta;
  type: string
}

export interface IBumperAddress {
  full: string;
  geo: IGeo;
  id: number;
  isPrivate: boolean;
  meta: IMeta;
  parsed: IParsedAddress;
  propertyRecordAvailable: boolean;
  propertyRelatedInfo: IPropertyRelatedInfo;
  type: string
}

export interface IPropertyRelatedInfo {
  isListingRecordAvailable: boolean;
  isPfcRecordAvailable: boolean;
  listingKey: string;
  parentPropertyId: number
}

export interface IParsedAddress {
  city: string;
  country: string;
  deliveryAddressLine: string;
  postDirection: string;
  preDirection: string;
  primaryAddressNumber: string;
  state: string;
  streetName: string;
  streetType: string;
  unitDesignator: string;
  unitNumber: string;
  zip4: string;
  zip5: string
}

export interface IGeo {
  latitude: number;
  longitude: number;
  precision: string
}

export interface ITheft {
  date: IDate
  dateOfRecovery: IDate;
  dateOfTheft: IDate;
  recordType: string;
  reportId: string;
  source: string;
  status: string;
  theftReportedState: string;
  vehicle: IVehicle
}

export interface ISpecs {
  cargoVolume: string;
  dimensions: string;
  passengerCapacity: number;
  towingCapacity: string;
  wheelbase: string;
  weight: string
}

export interface ISalvage {
  auction: string;
  date: IDate;
  status: string;
  damageType: string
}

export interface ISale {
  date: IDate;
  price: string;
  odometer: string;
  location: string;
  buyerType: string;
}

export interface ISafetyRating {
  agency: string;
  rating: string;
  test: string;
  date : IDate;
}

export interface IPrices {
  msrp: string;
  invoce: string;
  retail: string;
  traidIn: string;
}

export interface IRecalls {
  recallNumber: string;
  date: IDate;
  component: string;
  summary: string;
  consequence: string;
  remedy: string;
  notes: string;
}

export interface IRepairCost {
  component: string;
  averageCost: string;
  currency: string;
}

export interface IOwnershipHistory {
  ownerNumber: number;
  purchaseDate: IDate;
  endDate: IDate;
  state: string;
  type: string;
}

export interface IOwnershipCosts {
  insurance: string;
  maintenance: string;
  fuel: string;
  repairs: string;
}

export interface IOrigin {
  assemblycountry: string;
  plant: string;
  manufacturer: string;
}

export interface IOptions {
  code: string;
  description: string;
}

export interface IMileage {
  value: number;
  unit: string
  date: IDate
}
export interface IMetaInfo {
  createdAt: string;
  updatedAt: string;
  source: ISource;
}

export interface IMaintenanceSchedule {
  interval: string;
  service: string;
  mileage: string;
}

export interface ILien {
  holder: string;
  date: IDate;
  amount: string;
}

export interface ILease {
  lessor: string;
  startDate: IDate;
  endDate: IDate
}

export interface IImpound {
  agency: string;
  date: IDate;
  location: string;
}

export interface IImage {
  type: string;
  url: string;
}

export interface IExport {
  country: string;
  date: IDate
}

export interface IEstimatedMileage {
  value : number;
  unit : string
}

export interface IEquipment {
  code: string;
  description: string;
}

export interface IDesign {
  doors: number;
  drivetrain: string;
  engine: string;
  fueltype: string;
  transmission: string;
}

export interface IAccident {
  airbagsDeployed : boolean;
  crashLocation: string;
  crashSeverity: string;
  damageSeverity: string;
  date: IDate;
  estimatedDamage: number;
  estimatedSpeed: string;
  impactArea: string;
  insuranceCompany: string;
  licenseState: string;
  mannerOfCollision: string;
  objectoStruck: string;
  postedSpeed: string;
  reportId: string;
  reportingAgency: string;
  source: ISource
  type: string;
  vehicle: IVehicle;
}

export interface IVehicle {
  color: string;
  engineCode: string;
  make: string;
  mileage: string;
  model: string;
  moldingCode: string;
  productionDate: IDate;
  style: string;
  trim: string;
  trimColor: string;
  type: string;
  vin: string;
  year: number;
}

export interface IAuction {
  date: IDate;
  location: string;
  odometer: string;
  primaryDamage: string;
  secondaryDamage: string;
  seller: string;
  source: ISource;
  status: string;
  titleState: string;
  vin: string;
}

export interface IDamageHistory {
  count: number;
  records: IDamageRecord[];
}

export interface IDamageRecord {
  area: string;
  severity: string;
}

export interface ISource {
  sourceId: string;
  sourceName: string;
  order: number;
  maxPulls: number;
  isRankedSource: boolean;
  isIgnored: boolean
}

// ------------------------------------------------------------ BumperPlate ------------------------------------------------------------

export interface ILicensePlateProps {
  isLicensePlateModalOpen: boolean;
  pullData: (value: IFormAsset) => void;
  values: IFormAsset;
  onClose: () => void;
  setIsLicensePlateModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

export type GenericFormikErrrorVals = Record<string, string>;

export interface IDataResponse {
  accessTokenDoesNotExists: boolean;
  authorizeUrl: string;
  isError: boolean;
  errorDescription: string;
  fileInBytes: string;
  transactionId: string;
}

export interface IXmlDataResponse {
  accessTokenDoesNotExists: boolean;
  authorizeUrl: string;
  isError: boolean;
  errorDescription: string;
  adD123Response: IAdD123Response; 
}

export interface IAdD123Response {
  brandPresent: string; 
  brandReason: string;
  odometerReading: number;
  titlingJurisdiction: string;
  brandedTitle: boolean;
  branderState: string[] | string;
  brandName: string;
  brandDescription: string;
}

export interface IContactDetailsResponse {
  isMilitaryLendingActAvailable: boolean;
  addresses: Address2[];
}